import type { TableColumnLayout, TableColumnWidths } from "./types";

export const DEFAULT_COLUMN_MIN_WIDTH = 80;
/** Share given to every proportional column when none declares one. */
const DEFAULT_COLUMN_SHARE = 20;
/** The `px-4` on header and rows, which the minimum table width must include. */
export const TABLE_HORIZONTAL_PADDING = 32;

export const isFixedColumn = (column: TableColumnLayout) => column.fixedWidth !== undefined;

export const getColumnMinWidth = (column: TableColumnLayout) =>
  column.fixedWidth ?? column.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH;

const isShare = (value: number | undefined): value is number => typeof value === "number" && value > 0;

/**
 * The effective share of every proportional column: an override, else the column's own width, else
 * the mean of the shares that are defined (so a newly added column lands at an average size).
 */
export const resolveColumnShares = (
  columns: TableColumnLayout[],
  overrides: TableColumnWidths | undefined
): TableColumnWidths => {
  const proportional = columns.filter((column) => !isFixedColumn(column));
  const defined = proportional.reduce<number[]>((acc, column) => {
    const share = overrides?.[column.id] ?? column.width;
    if (isShare(share)) acc.push(share);
    return acc;
  }, []);
  const fallback = defined.length
    ? defined.reduce((sum, share) => sum + share, 0) / defined.length
    : DEFAULT_COLUMN_SHARE;

  return proportional.reduce<TableColumnWidths>((acc, column) => {
    const share = overrides?.[column.id] ?? column.width;
    acc[column.id] = isShare(share) ? share : fallback;
    return acc;
  }, {});
};

/** `grid-template-columns` for the utility columns followed by the data columns. */
export const buildGridTemplate = (
  columns: TableColumnLayout[],
  shares: TableColumnWidths,
  leadingFixedPx: number[]
): string =>
  [
    ...leadingFixedPx.map((px) => `${px}px`),
    ...columns.map((column) =>
      column.fixedWidth !== undefined
        ? `${column.fixedWidth}px`
        : `minmax(${getColumnMinWidth(column)}px, ${shares[column.id] ?? DEFAULT_COLUMN_SHARE}fr)`
    ),
  ].join(" ");

/** The narrowest the grid can get before it must scroll horizontally. */
export const sumMinWidths = (columns: TableColumnLayout[], leadingFixedPx: number[]): number =>
  leadingFixedPx.reduce((sum, px) => sum + px, 0) +
  columns.reduce((sum, column) => sum + getColumnMinWidth(column), 0) +
  TABLE_HORIZONTAL_PADDING;

export const roundShare = (share: number) => Math.round(share * 100) / 100;

interface NeighborResizeArgs {
  shares: TableColumnWidths;
  columnId: string;
  neighborId: string | null;
  deltaShare: number;
  minShares: TableColumnWidths;
}

/**
 * Grows `columnId` by `deltaShare` and shrinks its neighbour by the same amount, so the rest of the
 * grid holds still, clamped so neither drops below its minimum. Without a neighbour only the dragged
 * column changes.
 */
export const resizeNeighborShares = ({
  shares,
  columnId,
  neighborId,
  deltaShare,
  minShares,
}: NeighborResizeArgs): TableColumnWidths => {
  const current = shares[columnId] ?? DEFAULT_COLUMN_SHARE;
  const min = minShares[columnId] ?? 0;

  if (neighborId === null) {
    return { ...shares, [columnId]: Math.max(min, current + deltaShare) };
  }

  const neighbor = shares[neighborId] ?? DEFAULT_COLUMN_SHARE;
  const neighborMin = minShares[neighborId] ?? 0;
  const clamped = Math.min(Math.max(deltaShare, min - current), neighbor - neighborMin);

  return { ...shares, [columnId]: current + clamped, [neighborId]: neighbor - clamped };
};

/** The `px-2` on header and body cells, which a column's content width excludes. */
const CELL_HORIZONTAL_PADDING = 16;

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * Pixel widths of the proportional columns, resolved the way CSS grid sizes `minmax(<min>px, <share>fr)`
 * tracks: split the free space by share, freeze any column whose slice falls below its minimum at that
 * minimum, and split what remains among the rest, until no column is below its minimum.
 */
const resolveProportionalWidths = (
  columns: TableColumnLayout[],
  shares: TableColumnWidths,
  freeSpace: number
): TableColumnWidths => {
  const shareOf = (column: TableColumnLayout) => shares[column.id] ?? DEFAULT_COLUMN_SHARE;
  // Grid divides by at least 1, so shares summing below one leave space unfilled rather than stretching.
  const frSize = Math.max(0, freeSpace) / Math.max(1, sum(columns.map(shareOf)));
  const belowMin = columns.filter((column) => shareOf(column) * frSize < getColumnMinWidth(column));

  if (belowMin.length === 0) {
    return Object.fromEntries(columns.map((column) => [column.id, shareOf(column) * frSize]));
  }
  const frozen = Object.fromEntries(belowMin.map((column) => [column.id, getColumnMinWidth(column)]));
  const rest = columns.filter((column) => !belowMin.includes(column));
  return {
    ...frozen,
    ...resolveProportionalWidths(rest, shares, freeSpace - sum(belowMin.map(getColumnMinWidth))),
  };
};

/**
 * Each column's content width in pixels (its track less the cell padding), keyed by column id, derived
 * from the same inputs as {@link buildGridTemplate} plus the grid's rendered width. Calculating rather
 * than measuring keeps a column resize to one render per step: the widths follow the drag's shares in
 * the same render that moves the grid.
 */
export const resolveColumnContentWidths = (
  columns: TableColumnLayout[],
  shares: TableColumnWidths,
  leadingFixedPx: number[],
  gridWidth: number
): TableColumnWidths => {
  const fixed = columns.filter(isFixedColumn);
  const freeSpace = gridWidth - TABLE_HORIZONTAL_PADDING - sum(leadingFixedPx) - sum(fixed.map(getColumnMinWidth));
  const trackWidths = {
    ...Object.fromEntries(fixed.map((column) => [column.id, getColumnMinWidth(column)])),
    ...resolveProportionalWidths(
      columns.filter((column) => !isFixedColumn(column)),
      shares,
      freeSpace
    ),
  };
  return Object.fromEntries(
    Object.entries(trackWidths).map(([id, width]) => [id, Math.max(0, width - CELL_HORIZONTAL_PADDING)])
  );
};
